import { getBlogImageForPost } from '../data/blogImages.js';

const STRAPI_URL = import.meta.env.VITE_STRAPI_URL?.replace(/\/$/, '') || '';
const BLOG_ENDPOINT = import.meta.env.VITE_STRAPI_BLOG_ENDPOINT || '/api/blog-posts';

export const isStrapiConfigured = Boolean(STRAPI_URL);

const endpointPath = BLOG_ENDPOINT.startsWith('/') ? BLOG_ENDPOINT : `/${BLOG_ENDPOINT}`;

const buildStrapiUrl = (path, params = {}) => {
  const url = new URL(path.startsWith('http') ? path : `${STRAPI_URL}${path}`);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, value);
    }
  });

  return url.toString();
};

const unwrapEntry = (entry) => {
  if (!entry) return {};

  return entry.attributes
    ? {
        id: entry.id,
        documentId: entry.documentId,
        ...entry.attributes,
      }
    : entry;
};

const unwrapRelation = (relation) => {
  if (!relation) return null;
  if (relation.data) return unwrapEntry(relation.data);
  return unwrapEntry(relation);
};

const toAbsoluteMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${STRAPI_URL}${url.startsWith('/') ? url : `/${url}`}`;
};

const getMedia = (media) => {
  if (!media) return {};
  if (typeof media === 'string') return { url: toAbsoluteMediaUrl(media) };

  const entry = Array.isArray(media?.data) ? media.data[0] : media?.data || media;
  const item = unwrapEntry(entry);
  const formats = item.formats || {};
  const preferred = formats.large || formats.medium || formats.small || formats.thumbnail;

  return {
    url: toAbsoluteMediaUrl(preferred?.url || item.url),
    alt: item.alternativeText || item.alt || item.caption || '',
  };
};

const extractPlainText = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(extractPlainText).join(' ');
  if (typeof value === 'object') {
    if (typeof value.text === 'string') return value.text;
    if (value.children) return extractPlainText(value.children);
  }

  return '';
};

const stripMarkdown = (value) =>
  value
    .replace(/[#*_>`~-]/g, ' ')
    .replace(/\[(.*?)\]\(.*?\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();

const summarize = (value) => {
  const text = stripMarkdown(extractPlainText(value));
  if (!text) return '';
  return text.length > 165 ? `${text.slice(0, 162).trim()}...` : text;
};

const estimateReadTime = (value) => {
  const text = stripMarkdown(extractPlainText(value));
  const words = text ? text.split(/\s+/).length : 0;
  return `${Math.max(1, Math.ceil(words / 220))} min read`;
};

export const formatPostDate = (value) => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
};

const slugify = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const asText = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    return value.name || value.title || value.label || '';
  }
  return '';
};

export const normalizeBlogPost = (entry) => {
  const raw = unwrapEntry(entry);
  const category = unwrapRelation(raw.category);
  const author = unwrapRelation(raw.author);
  const media = getMedia(raw.heroImage || raw.image || raw.cover || raw.featuredImage);
  const body = raw.body || raw.content || raw.articleBody || raw.blocks || '';
  const title = raw.title || 'Untitled article';
  const categoryName = raw.categoryName || asText(category) || asText(raw.category) || 'Care Advice';

  return {
    id: raw.documentId || raw.id || raw.slug || slugify(title),
    slug: raw.slug || slugify(title),
    title,
    excerpt: raw.excerpt || raw.description || raw.summary || summarize(body),
    body,
    author: raw.authorName || asText(author) || asText(raw.author) || 'Odara Care Team',
    publishedAt: raw.publishedAt || raw.date || raw.createdAt || '',
    date: raw.dateLabel || formatPostDate(raw.publishedAt || raw.date || raw.createdAt),
    readTime: raw.readTime || estimateReadTime(body || raw.excerpt || ''),
    image: media.url || raw.imageUrl || getBlogImageForPost(categoryName, title),
    imageAlt: raw.imageAlt || media.alt || title,
    category: categoryName,
    featured: Boolean(raw.featured || raw.isFeatured),
    seoTitle: raw.seoTitle || raw.metaTitle || title,
    seoDescription:
      raw.seoDescription || raw.metaDescription || raw.excerpt || summarize(body),
    sources: raw.sources || [],
  };
};

const fetchJson = async (url) => {
  const response = await fetch(url, {
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Strapi request failed with ${response.status}`);
  }

  return response.json();
};

export const fetchBlogPosts = async () => {
  if (!isStrapiConfigured) return [];

  const url = buildStrapiUrl(endpointPath, {
    populate: '*',
    'sort[0]': 'publishedAt:desc',
    'pagination[pageSize]': '100',
  });
  const payload = await fetchJson(url);
  const entries = Array.isArray(payload.data) ? payload.data : [];

  return entries.map(normalizeBlogPost);
};

export const fetchBlogPostBySlug = async (slug) => {
  if (!isStrapiConfigured || !slug) return null;

  const url = buildStrapiUrl(endpointPath, {
    populate: '*',
    'filters[slug][$eq]': slug,
    'pagination[pageSize]': '1',
  });
  const payload = await fetchJson(url);
  const entry = Array.isArray(payload.data) ? payload.data[0] : payload.data;

  return entry ? normalizeBlogPost(entry) : null;
};
